import { flow } from '@helo/utils'

import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartDataOutput } from '../ChartData'
import type { ChartSeriesOutput } from '../ChartSeries'
import {
  type ChartCartesianOptions,
  chartCartesianOptions,
} from './ChartCartesian.options'
import {
  ChartCartesianInputParts,
  ChartCartesianOutputParts,
} from './ChartCartesian.parts'

export { chartCartesianOptions } from './ChartCartesian.options'
export type { ChartCartesianOptions } from './ChartCartesian.options'

/** Pipeline input consumed by ChartCartesian.setup. */
export type ChartCartesianInput = ChartCartesianOptions

export type ChartPlotOrientation = 'vertical' | 'horizontal'
export type ChartCategoryScaleKind = 'point' | 'band'

/**
 * Band-scale packing. `bandSize` is the bar/cluster thickness in px.
 * Omit `packedLength` to fill the plot and treat leftover space as Auto gap
 * (horizontal bars). Set `packedLength` to pin a shorter range so extra space
 * does not inflate gaps (grouped vertical).
 */
export type ChartBandPack = {
  paddingInner: number
  paddingOuter: number
  bandSize: number
  packedLength?: number
}

export type ChartCartesianSlice = {
  xDomain: string[]
  measureMin: number
  measureMax: number
  yDomain: [number, number]
  yTicks: number[]
  plotOrientation: ChartPlotOrientation
  categoryScale: ChartCategoryScaleKind
  bandPack?: ChartBandPack
  min?: number
  max?: number
  tickCount: number
}

/** Pipeline output slice — namespaced cartesian model. */
export type ChartCartesianOutput = {
  cartesian: ChartCartesianSlice
}

export interface ChartCartesianPriorOutput
  extends ChartCartesianOutput,
    ChartDataOutput,
    ChartSeriesOutput {
  contentNode: QueryOutput['contentNode']
}

const chartCartesianSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartCartesianInput,
    ChartCartesianOutput,
    ChartCartesianPriorOutput
  >(
    flow(
      ChartCartesianInputParts.setupInput(),
      ChartCartesianInputParts.setup_yAxisInput(),
      ChartCartesianOutputParts.setCartesianModel(),
      ChartCartesianOutputParts.setCategoryDomainFromRows(),
      ChartCartesianOutputParts.setMeasureExtent(),
      ChartCartesianOutputParts.setMeasureAxisDomain(),
      ChartCartesianOutputParts.niceUnforcedMeasureEnds(),
      ChartCartesianOutputParts.setMeasureTicks(),
      ChartCartesianOutputParts.setCategoryLayout(),
    ),
  )

export const ChartCartesian = {
  options: chartCartesianOptions,
  setup: chartCartesianSetup,
}
